import type { ActivityOf } from '@wyn/content';
import { OVERSWING_WARN, type WoodcuttingSim } from '@wyn/game-core';
import { palette } from '../lib/theme.ts';

/**
 * Canvas scene for the Logging Camp, ported from the Harrowmere prototype. Everything here is
 * cosmetic (Math.random is fine): the simulation decides the outcome, this only draws it. The scenery is
 * artwork with its own palette; the swing gauge and streak are UI and use theme tokens.
 */
export const W = 360;
export const H = 300;
const GROUND = 262;
const TAU = Math.PI * 2;
const S = { x: 142, y: 168 };
const R = 92;
const GRIP = 30;
const RG = R + 30;
const TH_HIT = 0.12, TH_REST = 1.0, TH_A = -0.6, TH_B = -2.95;
const thOf = (p: number) => TH_A + ((TH_B - TH_A) * Math.max(0, Math.min(100, p))) / 100;
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T,>(xs: T[]) => xs[Math.floor(Math.random() * xs.length)]!;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

const PAL = {
  'oak-log': { bark: ['#33251a', '#57402a', '#7a5a3a'], leaf: ['#1f3a1b', '#2f5526', '#467634', '#6b9a47'], wood: '#ebcf98', wood2: '#b98f5a' },
  'yew-log': { bark: ['#40201a', '#683426', '#8f5038'], leaf: ['#0f2418', '#193823', '#274f33', '#3b6a45'], wood: '#f0c49a', wood2: '#bf8960' },
} as const;

type Part =
  | { k: 'chip'; x: number; y: number; vx: number; vy: number; r: number; vr: number; w: number; h: number; life: number; col: string }
  | { k: 'leaf'; x: number; y: number; vx: number; vy: number; r: number; vr: number; ph: number; life: number; col: string }
  | { k: 'dust'; x: number; y: number; vx: number; vy: number; s: number; life: number };

export function createWoodScene(a: ActivityOf<'woodcutting'>) {
  const pal = PAL[a.log as keyof typeof PAL] ?? PAL['oak-log'];
  const TW = a.trunkWidth;
  const TL = S.x + R * Math.cos(TH_HIT) - 6;
  const TC = TL + TW / 2;
  const TOP = 110;
  const NY = S.y + R * Math.sin(TH_HIT) + 4;
  const HINGE = { x: TL + TW - 3, y: NY };

  const pines = Array.from({ length: 28 }, (_, i) => ({ x: i * 14 + rand(-5, 5), h: rand(40, 80), w: rand(14, 22), far: i % 2 }));
  const tufts = Array.from({ length: 46 }, () => ({ x: rand(0, W), y: rand(GROUND + 5, H - 3), h: rand(3, 7) }));
  const bark = Array.from({ length: Math.round(TW / 3.5) }, () => ({ x: rand(3, TW - 3), y0: rand(TOP + 30, GROUND - 30), len: rand(16, 55), c: Math.random() < 0.5 ? 0 : 2 }));
  const tall = a.log === 'yew-log' ? 1.2 : 0.85;
  const cy = TOP - a.crown * 0.3;
  const blobs = Array.from({ length: 17 }, () => {
    const t = rand(0, TAU), d = Math.sqrt(Math.random()) * a.crown * 0.62;
    return { x: TC + Math.cos(t) * d, y: cy + Math.sin(t) * d * tall, r: a.crown * rand(0.34, 0.5) };
  });
  const stumps = [{ x: 22, w: 16 }, { x: W - 26, w: 20 }];
  const scuffs: { y: number; l: number }[] = [];
  const parts: Part[] = [];

  let ang = TH_REST, notch = 0, shake = 0, shakeAmp = 0, canopyShake = 0, pulse = 0;
  let trailFrom: number | null = null, swingFrom = TH_REST;
  let fall = 0, fallV = 0, thudded = false, seenFx = 0, t = 0, lastState = 'ready';

  const chips = (n: number) => {
    for (let i = 0; i < n; i++)
      parts.push({ k: 'chip', x: TL + notch * 0.4, y: NY + rand(-4, 3), vx: rand(-200, -30), vy: rand(-240, -50), r: rand(0, TAU), vr: rand(-16, 16), w: rand(3, 7), h: rand(2, 3.5), life: 2.2, col: Math.random() < 0.6 ? pal.wood : pal.wood2 });
  };
  const leaves = (n: number, fromTree = true) => {
    for (let i = 0; i < n; i++) {
      const b = pick(blobs);
      parts.push({
        k: 'leaf', x: fromTree ? b.x + rand(-0.6, 0.6) * b.r : rand(HINGE.x + 40, W), y: fromTree ? b.y + rand(0, 0.6) * b.r : GROUND - rand(0, 50),
        vx: rand(-15, 15), vy: fromTree ? rand(20, 45) : rand(-60, 10), r: rand(0, TAU), vr: rand(-3, 3), ph: rand(0, TAU), life: 4, col: pick([...pal.leaf.slice(1)]),
      });
    }
  };
  const dust = (n: number) => {
    for (let i = 0; i < n; i++) parts.push({ k: 'dust', x: rand(HINGE.x + 30, W + 10), y: GROUND - rand(0, 8), vx: rand(-20, 20), vy: rand(-25, -5), s: rand(6, 14), life: rand(0.8, 1.4) });
  };

  const stepParts = (dt: number) => {
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i]!;
      if (p.k === 'chip') {
        p.vy += 720 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.r += p.vr * dt;
        if (p.y > GROUND + 3) { p.y = GROUND + 3; p.vy *= -0.25; p.vx *= 0.45; p.vr *= 0.4; }
        p.life -= dt;
      } else if (p.k === 'leaf') {
        if (p.y < GROUND + 4) { p.vy = Math.min(p.vy + 40 * dt, 45); p.x += (p.vx + Math.sin(t * 3 + p.ph) * 28) * dt; p.y += p.vy * dt; p.r += p.vr * dt; }
        p.life -= dt * 0.5;
      } else { p.x += p.vx * dt; p.y += p.vy * dt; p.s += 26 * dt; p.life -= dt; }
      if (p.life <= 0) parts.splice(i, 1);
    }
  };

  return function draw(g: CanvasRenderingContext2D, sim: WoodcuttingSim, dt: number) {
    t += dt;
    // React to what the simulation just did.
    for (; seenFx < sim.fx.length; seenFx++) {
      const f = sim.fx[seenFx]!;
      if (f.cue === 'chop') {
        const d = f.power ?? 0.5;
        chips(d >= 1 ? 16 : d >= 0.6 ? 10 : 4);
        if (d < 0.5) scuffs.push({ y: NY + rand(-12, 10), l: rand(4, 9) });
        canopyShake = 3 + d * 5; shake = 0.15; shakeAmp = 1 + d * 2.5;
        leaves(1 + Math.floor(Math.random() * (2 + Math.round(d * 3))));
        if (sim.streak > 1) pulse = 1;
      }
    }
    // Axe angle follows the state, eased.
    if (sim.state !== lastState) {
      if (sim.state === 'swing') { swingFrom = ang; trailFrom = ang; }
      lastState = sim.state;
    }
    const st = sim.stateTime;
    if (sim.state === 'ready' || sim.state === 'recover') ang += (TH_REST - ang) * Math.min(1, dt * 8);
    else if (sim.state === 'charge') ang += (thOf(sim.power) - ang) * Math.min(1, dt * 24);
    else if (sim.state === 'swing') { const k = Math.min(1, st / 0.13); ang = swingFrom + (TH_HIT - swingFrom) * k * k; }
    else if (sim.state === 'stuck') { ang = TH_HIT; if (st > 0.08) trailFrom = null; }
    else if (sim.state === 'stagger') { const k = Math.min(1, st / 0.7); ang = TH_B - 0.3 + 0.22 * Math.sin(st * 20) * (1 - k); }
    if (sim.felled) { ang = TH_HIT; trailFrom = null; }

    notch += (cutFraction(sim) * TW * 0.78 - notch) * Math.min(1, dt * 14);
    canopyShake *= Math.max(0, 1 - dt * 5);
    if (shake > 0) shake -= dt;
    if (sim.felled && !thudded) {
      fallV += (0.7 + 7.5 * Math.sin(fall)) * dt; fall += fallV * dt;
      if (fall >= 1.52) { fall = 1.52; thudded = true; shake = 0.5; shakeAmp = 7; dust(26); leaves(22, false); }
    }
    stepParts(dt);
    pulse = Math.max(0, pulse - dt * 3);

    // ---- render ----
    const line = (x1: number, y1: number, x2: number, y2: number) => { g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.stroke(); };
    const grass = () => {
      const gr = g.createLinearGradient(0, GROUND, 0, H);
      gr.addColorStop(0, '#46552e'); gr.addColorStop(1, '#27321c');
      g.fillStyle = gr; g.fillRect(-20, GROUND, W + 40, H - GROUND + 20);
      g.strokeStyle = '#5f7340'; g.lineWidth = 1.2;
      for (const tf of tufts) { g.beginPath(); g.moveTo(tf.x, tf.y); g.lineTo(tf.x - 2, tf.y - tf.h); g.moveTo(tf.x, tf.y); g.lineTo(tf.x + 2, tf.y - tf.h); g.stroke(); }
    };
    const background = () => {
      const sky = g.createLinearGradient(0, 0, 0, GROUND);
      sky.addColorStop(0, '#1e2a36'); sky.addColorStop(0.65, '#5b5248'); sky.addColorStop(1, '#8a6a48');
      g.fillStyle = sky; g.fillRect(0, 0, W, GROUND);
      const sun = g.createRadialGradient(60, 150, 4, 60, 150, 110);
      sun.addColorStop(0, 'rgba(255,214,150,.55)'); sun.addColorStop(1, 'rgba(255,214,150,0)');
      g.fillStyle = sun; g.fillRect(0, 0, W, GROUND);
      g.fillStyle = '#2c3530'; g.beginPath(); g.moveTo(0, 200);
      for (let x = 0; x <= W; x += 20) g.lineTo(x, 196 + Math.sin(x * 0.02) * 14);
      g.lineTo(W, GROUND); g.lineTo(0, GROUND); g.fill();
      for (const p of pines) {
        g.fillStyle = p.far ? '#24302a' : '#1a241e';
        const base = GROUND - (p.far ? 26 : 8);
        for (let k = 0; k < 3; k++) {
          const y = base - k * p.h * 0.28, w = p.w * (1 - k * 0.25);
          g.beginPath(); g.moveTo(p.x - w, y); g.lineTo(p.x, y - p.h * 0.45); g.lineTo(p.x + w, y); g.fill();
        }
      }
      grass();
      for (const s of stumps) {
        g.fillStyle = '#4a3625'; g.fillRect(s.x - s.w / 2, GROUND - 10, s.w, 12);
        g.fillStyle = '#c9a877'; g.beginPath(); g.ellipse(s.x, GROUND - 10, s.w / 2, 3, 0, 0, TAU); g.fill();
      }
    };
    const canopy = (cs: number) => {
      for (let k = 0; k < 4; k++) {
        const sc = 1 - k * 0.2, ox = -k * a.crown * 0.07 + cs, oy = -k * a.crown * 0.08;
        g.fillStyle = pal.leaf[k]!; g.beginPath();
        for (const b of blobs) { g.moveTo(b.x + ox + b.r * sc, b.y + oy); g.arc(b.x + ox, b.y + oy, b.r * sc, 0, TAU); }
        g.fill();
      }
    };
    const tree = (part: 'all' | 'stump' | 'top', cs: number) => {
      g.save(); g.beginPath();
      if (part === 'stump') g.rect(-50, NY, W + 100, H);
      else if (part === 'top') g.rect(-600, -600, 1600, NY + 600);
      else g.rect(-600, -600, 1600, 1600);
      g.clip();
      const tg = g.createLinearGradient(TL, 0, TL + TW, 0);
      tg.addColorStop(0, pal.bark[2]); tg.addColorStop(0.45, pal.bark[1]); tg.addColorStop(1, pal.bark[0]);
      g.fillStyle = tg; g.beginPath();
      g.moveTo(TL + 3, TOP - 10); g.lineTo(TL + TW - 3, TOP - 10); g.lineTo(TL + TW, GROUND - 16);
      g.quadraticCurveTo(TL + TW + 2, GROUND - 2, TL + TW + 12, GROUND + 1); g.lineTo(TL - 12, GROUND + 1);
      g.quadraticCurveTo(TL - 2, GROUND - 2, TL, GROUND - 16); g.closePath(); g.fill();
      g.lineWidth = 1.4;
      for (const b of bark) {
        g.strokeStyle = b.c ? 'rgba(255,230,190,.10)' : 'rgba(0,0,0,.28)';
        g.beginPath(); g.moveTo(TL + b.x, b.y0); g.bezierCurveTo(TL + b.x + 2, b.y0 + b.len * 0.3, TL + b.x - 2, b.y0 + b.len * 0.7, TL + b.x, b.y0 + b.len); g.stroke();
      }
      g.strokeStyle = 'rgba(235,207,152,.5)'; g.lineWidth = 1.5;
      for (const s of scuffs) { g.beginPath(); g.moveTo(TL, s.y); g.lineTo(TL + s.l, s.y + s.l * 0.4); g.stroke(); }
      if (notch > 0.5) {
        const up = NY - 5 - notch * 0.6, dn = NY + 2 + notch * 0.12;
        g.fillStyle = pal.wood; g.beginPath(); g.moveTo(TL - 1, up); g.lineTo(TL + notch, NY); g.lineTo(TL - 1, dn); g.closePath(); g.fill();
        g.fillStyle = pal.wood2; g.beginPath(); g.moveTo(TL - 1, up); g.lineTo(TL + notch, NY); g.lineTo(TL + notch * 0.55, NY - 1); g.closePath(); g.fill();
        g.strokeStyle = 'rgba(40,24,12,.55)'; g.lineWidth = 1; g.beginPath(); g.moveTo(TL - 1, up); g.lineTo(TL + notch, NY); g.lineTo(TL - 1, dn); g.stroke();
      }
      g.restore();
      if (part !== 'stump') canopy(cs);
    };
    const axe = (th: number) => {
      const c = Math.cos(th), s = Math.sin(th);
      g.lineCap = 'round';
      g.strokeStyle = '#24170c'; g.lineWidth = 7; line(S.x + c * (GRIP - 12), S.y + s * (GRIP - 12), S.x + c * (R + 3), S.y + s * (R + 3));
      g.strokeStyle = '#9d6d3d'; g.lineWidth = 4.5; line(S.x + c * (GRIP - 12), S.y + s * (GRIP - 12), S.x + c * (R + 3), S.y + s * (R + 3));
      g.save(); g.translate(S.x + c * R, S.y + s * R); g.rotate(th);
      const hg = g.createLinearGradient(0, -8, 0, 18);
      hg.addColorStop(0, '#50585e'); hg.addColorStop(0.55, '#a7b1b7'); hg.addColorStop(1, '#f2f6f8');
      g.fillStyle = hg; g.beginPath();
      g.moveTo(-6, -8); g.lineTo(7, -8); g.lineTo(6, 3); g.quadraticCurveTo(16, 8, 15, 18);
      g.quadraticCurveTo(0, 21, -12, 16); g.quadraticCurveTo(-8, 8, -5, 3); g.closePath(); g.fill();
      g.strokeStyle = '#2c3236'; g.lineWidth = 1.2; g.stroke();
      g.restore();
    };
    const woodcutter = (th: number, lean: number) => {
      const dx = Math.cos(th), dy = Math.sin(th);
      const hip = { x: S.x - 8 - lean * 6, y: S.y + 52 }, sh = { x: S.x, y: S.y };
      const hand = { x: sh.x + dx * GRIP, y: sh.y + dy * GRIP };
      g.lineCap = 'round'; g.lineJoin = 'round';
      g.strokeStyle = '#2b241d'; g.lineWidth = 9;
      line(hip.x, hip.y, hip.x - 17, GROUND - 4); line(hip.x, hip.y, hip.x + 15, GROUND - 4);
      g.fillStyle = '#17120d'; g.fillRect(hip.x - 26, GROUND - 7, 15, 7); g.fillRect(hip.x + 10, GROUND - 7, 15, 7);
      g.strokeStyle = '#4c2c1c'; g.lineWidth = 8; line(sh.x - 5, sh.y + 3, hand.x, hand.y);
      g.fillStyle = '#7a4a2e'; g.beginPath();
      g.moveTo(sh.x - 15, sh.y - 5); g.lineTo(sh.x + 9, sh.y - 3); g.lineTo(hip.x + 13, hip.y + 6); g.lineTo(hip.x - 15, hip.y + 6); g.closePath(); g.fill();
      g.fillStyle = '#231910'; g.fillRect(hip.x - 15, hip.y - 7, 28, 5);
      g.fillStyle = '#c69a3e'; g.fillRect(hip.x - 2, hip.y - 7, 5, 5);
      const hx = sh.x + 3 + lean * 5, hy = sh.y - 19;
      g.fillStyle = '#d6a37a'; g.beginPath(); g.arc(hx, hy, 10, 0, TAU); g.fill();
      g.fillStyle = '#5b3b22'; g.beginPath(); g.arc(hx + 4, hy + 5, 6, 0.1, Math.PI - 0.4); g.fill();
      g.fillStyle = '#4a5a38'; g.beginPath(); g.arc(hx - 1, hy - 1, 12, Math.PI * 0.95, Math.PI * 2.1); g.lineTo(hx - 12, hy + 12); g.closePath(); g.fill();
      g.fillStyle = '#1b1510'; g.beginPath(); g.arc(hx + 5, hy - 1, 1.4, 0, TAU); g.fill();
      axe(th);
      g.strokeStyle = '#7a4a2e'; g.lineWidth = 8; line(sh.x, sh.y, hand.x, hand.y);
      g.fillStyle = '#d6a37a'; g.beginPath(); g.arc(hand.x, hand.y, 4.5, 0, TAU); g.fill();
    };
    const trail = () => {
      if (trailFrom === null) return;
      const a0 = Math.max(trailFrom, ang - 1.1), a1 = ang;
      if (a1 - a0 < 0.02) return;
      const gr = g.createRadialGradient(S.x, S.y, R - 12, S.x, S.y, R + 20);
      gr.addColorStop(0, 'rgba(255,236,200,0)'); gr.addColorStop(0.6, 'rgba(255,236,200,.28)'); gr.addColorStop(1, 'rgba(255,236,200,0)');
      g.fillStyle = gr; g.beginPath(); g.arc(S.x, S.y, R + 20, a0, a1); g.arc(S.x, S.y, R - 12, a1, a0, true); g.closePath(); g.fill();
    };
    const gauge = () => {
      const live = sim.state === 'ready' || sim.state === 'charge';
      const c = palette();
      g.save(); g.globalAlpha = live ? 1 : 0.3; g.lineCap = 'butt';
      const seg = (p0: number, p1: number, w: number, col: string) => { g.strokeStyle = col; g.lineWidth = w; g.beginPath(); g.arc(S.x, S.y, RG, thOf(p1), thOf(p0)); g.stroke(); };
      seg(0, 100, 18, 'rgba(240,226,196,.16)');
      seg(0, 100, 14, 'rgba(8,6,4,.7)');
      seg(0, 100, 9, c.rule);
      const inSweet = sim.state === 'charge' && sim.power >= sim.sweet[0] && sim.power <= sim.sweet[1];
      seg(sim.sweet[0], sim.sweet[1], 9, inSweet ? c['gilt-bright'] : c.gilt);
      seg(OVERSWING_WARN, 100, 9, c.blood);
      if (sim.power > 0) seg(0, sim.power, 4, c.ember);
      g.strokeStyle = 'rgba(0,0,0,.55)'; g.lineWidth = 1.5;
      for (let p = 10; p < 100; p += 10) {
        const th = thOf(p);
        line(S.x + Math.cos(th) * (RG - 5), S.y + Math.sin(th) * (RG - 5), S.x + Math.cos(th) * (RG + 5), S.y + Math.sin(th) * (RG + 5));
      }
      if (sim.state === 'charge') {
        const th = thOf(sim.power);
        g.fillStyle = c.ink; g.shadowColor = inSweet ? c['gilt-bright'] : c.ember; g.shadowBlur = 12;
        g.beginPath(); g.arc(S.x + Math.cos(th) * RG, S.y + Math.sin(th) * RG, 5.5, 0, TAU); g.fill();
      }
      g.restore();
    };
    const drawParts = () => {
      for (const p of parts) {
        g.save(); g.globalAlpha = Math.min(1, p.life);
        if (p.k === 'dust') { g.fillStyle = 'rgba(160,140,110,.35)'; g.beginPath(); g.arc(p.x, p.y, p.s, 0, TAU); g.fill(); }
        else {
          g.translate(p.x, p.y); g.rotate(p.r); g.fillStyle = p.col;
          if (p.k === 'chip') g.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          else { g.beginPath(); g.ellipse(0, 0, 4, 2, 0, 0, TAU); g.fill(); }
        }
        g.restore();
      }
    };

    g.save();
    if (shake > 0) g.translate(rand(-1, 1) * shakeAmp * Math.min(1, shake * 4), rand(-1, 1) * shakeAmp * Math.min(1, shake * 4));
    background();
    const cs = Math.sin(t * 42) * canopyShake;
    if (!sim.felled) tree('all', cs);
    else {
      tree('stump', 0);
      const k = clamp((fall - 0.45) / 1.05, 0, 1), drop = (GROUND - 4 - NY) * k * k * (3 - 2 * k);
      g.save(); g.translate(HINGE.x + drop * 0.35, HINGE.y + drop); g.rotate(fall); g.translate(-HINGE.x, -HINGE.y); tree('top', 0); g.restore();
      g.save(); g.beginPath(); g.rect(HINGE.x + 4, GROUND, W, H); g.clip(); grass(); g.restore();
    }
    const lean = sim.state === 'charge' ? (-sim.power / 100) * 0.8 : sim.state === 'stuck' || sim.state === 'swing' ? 0.9 : sim.state === 'stagger' ? -1.4 : 0;
    trail();
    woodcutter(ang, lean);
    drawParts();
    if (!sim.result) gauge();
    if (sim.streak > 1 && !sim.felled) {
      g.save(); g.textAlign = 'right'; g.font = `bold ${20 + pulse * 8}px Georgia, serif`;
      const c = palette();
      g.shadowColor = c.ember; g.shadowBlur = 8 + pulse * 14; g.fillStyle = c['gilt-bright'];
      g.fillText(`🔥 ×${sim.streak}`, W - 12, 30); g.restore();
    }
    g.restore();
  };

  function cutFraction(sim: WoodcuttingSim) {
    return clamp(1 - sim.hp / a.hp, 0, 1);
  }
}
